/**
 * ABOUTME: Main SmartQuote component that orchestrates the entire quote request flow
 * ABOUTME: Manages state and transitions between selection, sending, and success views
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Tradesperson } from '@/lib/types';
import { useTradeQuoteRequest } from '@/hooks/useTradeQuoteRequest';
import { debug } from '@/lib/utils';
import { toast } from 'sonner';
import { generateMessageTemplate, updateMessageWithName } from './smartQuoteUtils';
import { SmartQuoteSelectionView } from './SmartQuoteSelectionView';
import { SmartQuoteSendView } from './SmartQuoteSendView';
import { SmartQuoteSuccessView } from './SmartQuoteSuccessView';

interface SmartQuoteProps {
  initialValue?: string;
  postcode?: string;
  tradespeople: Tradesperson[];
  isLoading?: boolean;
  onComplete?: () => void;
  onStateChange?: (state: 'selection' | 'contact' | 'success') => void;
}

export function SmartQuote({
  initialValue = '',
  postcode,
  tradespeople,
  isLoading = false,
  onComplete,
  onStateChange
}: SmartQuoteProps) {
  // State for tracking which section is visible
  const [uiState, setUiState] = useState<'selection' | 'contact_send' | 'success'>('selection');

  // Notify parent component when state changes
  useEffect(() => {
    if (onStateChange) {
      onStateChange(uiState);
    }
  }, [uiState, onStateChange]);

  // Input state
  const [userPostcode, setUserPostcode] = useState(postcode || '');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [visibleCategories, setVisibleCategories] = useState<string[]>([]);

  // Message and contact state
  const [fullMessage, setFullMessage] = useState('');
  const [name, setName] = useState('');
  
  // Refs for horizontal scroll
  const categoriesScrollRef = useRef<HTMLDivElement>(null);

  // Scroll state for navigation
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Function to check scroll position
  const checkScrollPosition = useCallback(() => {
    const container = categoriesScrollRef.current;
    if (container) {
      // Can scroll left if scrolled right at all
      setCanScrollLeft(container.scrollLeft > 0);

      // Can scroll right if there's more content to scroll to
      setCanScrollRight(
        container.scrollWidth > container.clientWidth &&
        container.scrollLeft < container.scrollWidth - container.clientWidth
      );
    }
  }, []);

  // Scroll action handlers
  const handleScrollLeft = () => {
    if (categoriesScrollRef.current) {
      categoriesScrollRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const handleScrollRight = () => {
    if (categoriesScrollRef.current) {
      categoriesScrollRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  // Trade selection hooks from useTradeQuoteRequest
  const {
    isSubmitting,
    handleTradeSelection,
    submitQuoteRequests,
    selectedTradespeople,
    resetSelection,
    setMessage,
    message
  } = useTradeQuoteRequest(undefined, true);

  // UI state for filtered trades
  const [filteredTradespeople, setFilteredTradespeople] = useState<Tradesperson[]>([]);

  // Get unique categories from tradespeople and prioritize common ones
  useEffect(() => {
    // Priority categories in order (most needed first)
    const priorityCategories = [
      'plumber',
      'heating engineer',
      'electrician',
      'roofer',
      'builder',
      'decorator',
      'carpenter',
      'gardener'
    ];

    let extractedCategories: string[] = [];

    // Try to extract categories from tradespeople if they exist and have data
    if (tradespeople && tradespeople.length > 0) {
      // Extract unique categories from all tradespeople, with fallbacks
      const allCategories = Array.from(
        new Set(
          tradespeople
            .map(t => {
              // Use getTradeType which already has proper fallback logic
              if (t.trade_type || t.category) {
                const normalizedType = (t.trade_type || t.category)?.toLowerCase();
                debug.log('Found trade type:', normalizedType);
                return normalizedType;
              }
              return null;
            })
            .filter(Boolean) as string[]
        )
      );

      extractedCategories = allCategories;
      debug.log('Extracted categories from tradespeople:', extractedCategories);
    }

    // IMPORTANT: If no categories found in data, fall back to showing common ones
    if (extractedCategories.length === 0) {
      debug.log('No categories found in data, using default categories');
      extractedCategories = [...priorityCategories];
    }

    // Sort categories by priority (putting priority ones first in the order specified)
    const sortedCategories = [...extractedCategories].sort((a, b) => {
      const aIndex = priorityCategories.indexOf(a);
      const bIndex = priorityCategories.indexOf(b);

      // If both categories are in priority list, sort by priority order
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }

      // If only a is in priority list, it comes first
      if (aIndex !== -1) return -1;

      // If only b is in priority list, it comes first
      if (bIndex !== -1) return 1;

      // If neither is in priority list, keep original order
      return 0;
    });

    setVisibleCategories(sortedCategories);
    debug.log('Final visible categories:', sortedCategories);
  }, [tradespeople]);

  // Generate a template message
  useEffect(() => {
    const template = generateMessageTemplate(initialValue, userPostcode, name);
    setFullMessage(template);
    setMessage(template);
  }, [initialValue, userPostcode, name, setMessage]);

  // Filter tradespeople when category changes
  useEffect(() => {
    if (selectedCategory) {
      debug.log(`Filtering by category: ${selectedCategory}`);
      if (tradespeople && tradespeople.length > 0) {
        const filtered = tradespeople.filter(t => {
          const normalizedTradeType = (t.trade_type || t.category || '').toLowerCase();
          return normalizedTradeType === selectedCategory.toLowerCase();
        });
        debug.log(`Filtered ${tradespeople.length} tradespeople for category "${selectedCategory}":`, filtered.length);
        setFilteredTradespeople(filtered);
      } else {
        setFilteredTradespeople([]);
      }
    } else {
      setFilteredTradespeople(tradespeople || []);
    }
  }, [selectedCategory, tradespeople]);

  // Handle moving to contact & send state
  const moveToContinue = useCallback(() => {
    if (selectedTradespeople.length === 0) {
      toast.warning('Please select at least one tradesperson');
      return;
    }

    setUiState('contact_send');
  }, [selectedTradespeople.length]);

  // Handle email submission
  const handleEmailSubmit = useCallback(async (contactEmail: string) => {
    try {
      // Update the message with the actual name if needed
      if (name && fullMessage.includes('[Your name]')) {
        const updatedMessage = updateMessageWithName(fullMessage, name);
        setFullMessage(updatedMessage);
        setMessage(updatedMessage);
      }

      // Submit the quote request with empty contact info
      // The actual contact info will come from the user's email client
      const success = await submitQuoteRequests('email', contactEmail);

      if (success) {
        // Record success in analytics if applicable
        return true;
      } else {
        toast.error('Failed to record quote request');
        return false;
      }
    } catch (error) {
      debug.error('Error submitting quote request:', error);
      return false;
    }
  }, [
    fullMessage,
    name,
    submitQuoteRequests,
    setMessage
  ]);

  // Handle text message submission for a specific tradesperson
  const handleTextSubmit = useCallback(async (tradeId: string) => {
    try {
      // Here we would ideally have a single-recipient version of submitQuoteRequests
      // But for now we'll track it with the general method
      // Phone number is blank because it comes from the user's SMS app
      const success = await submitQuoteRequests('phone', '', [tradeId]);
      return success;
    } catch (error) {
      debug.error('Error recording text message submission:', error);
      return false;
    }
  }, [submitQuoteRequests]);

  // Reset the form
  const resetForm = useCallback(() => {
    setSelectedCategory(null);
    setFullMessage('');
    setName('');
    resetSelection();
    setUiState('selection');
  }, [resetSelection]);

  return (
    <div className="w-full">
      {/* Selection View */}
      {uiState === 'selection' && (
        <SmartQuoteSelectionView
          fullMessage={fullMessage}
          setFullMessage={(msg) => {
            setFullMessage(msg);
            setMessage(msg);
          }}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          tradespeople={tradespeople}
          filteredTradespeople={filteredTradespeople}
          selectedTradespeople={selectedTradespeople}
          handleTradeSelection={handleTradeSelection}
          visibleCategories={visibleCategories}
          moveToContinue={moveToContinue}
          canScrollLeft={canScrollLeft}
          canScrollRight={canScrollRight}
          handleScrollLeft={handleScrollLeft}
          handleScrollRight={handleScrollRight}
          checkScrollPosition={checkScrollPosition}
        />
      )}

      {/* Contact & Send View */}
      {uiState === 'contact_send' && (
        <SmartQuoteSendView
          message={fullMessage}
          selectedTradespeople={selectedTradespeople}
          tradespeople={tradespeople}
          initialName={name}
          onSendEmail={handleEmailSubmit}
          onSendText={handleTextSubmit}
          onComplete={() => setUiState('success')}
          onBack={() => setUiState('selection')}
        />
      )}

      {/* Success View */}
      {uiState === 'success' && (
        <SmartQuoteSuccessView
          selectedTradespeople={selectedTradespeople}
          resetForm={resetForm}
        />
      )}
    </div>
  );
}